import type { BusinessDate } from '../date/business-date';
import type { IsoWeek } from '../date/iso-week';
import { compareNames } from '../shared/naming';
import type {
  ActivityId,
  DisciplineId,
  ExecutionResourceId,
  ServiceId,
  TeamId,
} from '../shared/ids';
import type { ResourceType } from '../workforce/execution-resource';
import type { ActivityStatus } from '../structure/activity';
import { isClosed } from '../structure/activity';
import {
  CapacityCalculator,
  capacityStatusOf,
  spansFromAllocations,
  type CapacityBalance,
  type CapacityStatus,
} from './capacity-calculator';
import type { ResourceCapacityIndex } from './resource-capacity';
import { countsTowardCapacity, type WeeklyAllocation } from './weekly-allocation';
import type { WeeklyPlanStatus } from './weekly-plan';

/**
 * O quadro da semana.
 *
 * ```text
 * SEMANA 34                    seg    ter    qua    qui    sex    total
 * João da Silva — Pedreiro
 *   Alvenaria — 2º Pavimento    4h     4h     9h     9h     8h      34h
 *   Estrutural — 1º Pavimento   5h     5h      —      —      —      10h
 *   Total do dia               9h/9h  9h/9h  9h/9h  9h/9h  8h/8h  44h/44h
 * ```
 *
 * Três eixos — recurso, atividade e **dia** —, porque é assim que o serviço é montado no
 * canteiro: na segunda o pedreiro faz quatro horas de alvenaria e cinco de estrutura. O
 * dia é a unidade de lançamento; a semana é a unidade de planejamento, e é dela que sai
 * o saldo exibido (RN-043, RN-080).
 *
 * Montagem **pura**: recebe recursos, atividades e alocações já carregados e devolve a
 * grade com totais, saldo e estado. Nenhuma consulta por recurso e nenhuma por atividade
 * — 30 recursos × 20 atividades custam o mesmo número de leituras que um recurso só, que
 * é o critério de aceite de desempenho da fase.
 */

export interface BoardResource {
  readonly resourceId: ExecutionResourceId;
  /** Funcionário da obra ou serviço terceirizado — o resumo separa os dois. */
  readonly type: ResourceType;
  readonly name: string;
  /** Função do funcionário ou empresa do serviço — o que aparece ao lado do nome. */
  readonly detail: string;
  readonly teamId?: TeamId;
  readonly teamName?: string;
  /** Recurso inativo ou funcionário não `ACTIVE` não recebe **nova** alocação (RN-086). */
  readonly isAllocatable: boolean;
}

export interface BoardActivity {
  readonly activityId: ActivityId;
  /** A disciplina da frente — é ela que filtra a lista de serviços na alocação. */
  readonly disciplineId: DisciplineId;
  /** `"Alvenaria — 3º Pavimento"` — o rótulo do requisito. */
  readonly label: string;
  readonly towerName: string;
  readonly floorName: string;
  readonly disciplineName: string;
  readonly status: ActivityStatus;
  /**
   * Como a frente era medida.
   *
   * @deprecated A unidade desceu para o serviço na Fase 21 (RN-232) — alvenaria mede
   * parede em m² e verga em unidade, e a frente nunca soube dizer qual. Continua aqui só
   * enquanto a Estrutura ainda mostra a unidade da disciplina; some com ela.
   */
  readonly unitName?: string;
  readonly unitDecimals?: number;
  /** Impedimento aberto (Fase 5). Alocar é permitido, mas avisado (RN-085). */
  readonly hasBlocker?: boolean;
  /**
   * A disciplina da frente foi desativada no cadastro.
   *
   * A frente continua dando nome ao que já foi lançado nela, mas não recebe planejamento
   * novo — a mesma regra do serviço desativado (RN-230).
   */
  readonly disciplineInactive?: boolean;
}

/**
 * A equipe que a grade nomeia (Fase 30, D10).
 *
 * A célula traz a alocação inteira, e com ela a marca da equipe planejada
 * (`allocation.plannedTeamId`, RN-422). É por essa marca, e não pela equipe de hoje da pessoa
 * (`BoardResource.teamId`), que a grade junta as pessoas na linha da equipe: quem trocou de
 * equipe na quarta continua na linha da antiga na segunda. Esta lista só dá o nome — inclusive o
 * da equipe que saiu do cadastro depois de planejada.
 */
export interface BoardTeam {
  readonly teamId: TeamId;
  readonly name: string;
  /** Desativada ou excluída: continua nomeando o que já foi planejado, e não recebe mais. */
  readonly isActive: boolean;
}

/** Um dia da semana, no cabeçalho da grade. */
export interface BoardDay {
  readonly date: string;
  /** `"seg"`, `"ter"`… */
  readonly weekdayLabel: string;
  /** `"17/08"` */
  readonly shortLabel: string;
  /** O canteiro abre neste dia? Serve para apagar visualmente sábado e domingo. */
  readonly isWorkday: boolean;
}

/** A célula: as horas de um recurso, numa atividade, num dia. */
export interface BoardCell {
  readonly date: string;
  readonly allocation?: WeeklyAllocation;
}

/**
 * O serviço da linha — o que a obra chama de atividade (ADR-135).
 *
 * Vem resolvido, e não como id, pela mesma razão da unidade: quem desenha a grade precisa
 * do nome, e não pode sair consultando o catálogo dentro do render.
 */
export interface BoardService {
  readonly serviceId: ServiceId;
  readonly name: string;
  /** A disciplina a que ele pertence — o filtro que reduz 250 serviços a meia dúzia. */
  readonly disciplineId: DisciplineId;
  /**
   * Desativado não recebe planejamento novo (RN-230) e continua nomeando o que já existe.
   *
   * Por isso ele está na lista e marcado, em vez de fora dela: a linha planejada semana
   * passada precisa do nome, e o select da alocação precisa não o oferecer.
   */
  readonly isActive: boolean;
  /**
   * Como o serviço é medido — `"m²"`, `"m³"`. Ausente = serviço sem unidade cadastrada.
   *
   * Vem resolvida do catálogo, e não como id: quem desenha a grade precisa do rótulo e da
   * precisão, e não pode sair consultando unidade por linha dentro do render. Ausente, a
   * meta não aparece e a tela diz onde cadastrar (RN-232).
   */
  readonly unitName?: string;
  readonly unitDecimals?: number;
}

/**
 * Uma linha dentro do bloco de um recurso: uma frente, **um serviço**.
 *
 * A mesma frente aparece em tantas linhas quantos forem os serviços que a pessoa faz nela
 * na semana — quatro horas de fôrma e quatro de armadura são dois trabalhos, com metas em
 * unidades diferentes. Quem desenha agrupa as linhas da mesma frente sob um cabeçalho: um
 * funcionário dificilmente troca de disciplina no mesmo dia, e quando troca isso precisa
 * ficar evidente.
 *
 * `service` ausente é a linha que não sabe o que se fez ali — a de antes da Fase 21, a que
 * o RDO lançou numa frente sem serviço, a que a proposta automática não teve como escolher.
 * Ela guarda horas e não mede.
 */
export interface BoardActivityRow {
  readonly activity: BoardActivity;
  readonly service?: BoardService;
  /** Sete células, na ordem da semana — segunda a domingo. */
  readonly cells: readonly BoardCell[];
  readonly plannedQuarterHours: number;
}

/** O total de um dia para um recurso — é aqui que o conflito de capacidade aparece. */
export interface BoardDayTotal {
  readonly date: string;
  readonly plannedQuarterHours: number;
  /** Ausente = sem limite declarado (RN-047). */
  readonly capacityQuarterHours?: number;
  readonly status: CapacityStatus;
}

export interface BoardRow {
  readonly resource: BoardResource;
  readonly activities: readonly BoardActivityRow[];
  readonly dayTotals: readonly BoardDayTotal[];
  readonly plannedQuarterHours: number;
  /** Saldo **da semana** — o que a tela exibe (RN-043). */
  readonly balance: CapacityBalance;
}

export interface WeekSummary {
  /** Capacidade da **mão de obra própria** com limite declarado e apta a trabalhar. */
  readonly availableQuarterHours: number;
  /** Horas planejadas da mão de obra própria. */
  readonly plannedQuarterHours: number;
  readonly balanceQuarterHours: number;
  /** Fração da capacidade não planejada, entre 0 e 1. */
  readonly idleRatio: number;
  readonly overloadedCount: number;
  readonly atLimitCount: number;
  readonly withBalanceCount: number;
  readonly allocatedResourceCount: number;
  /** Dias-recurso em que o planejado passou da jornada — o excesso que importa (RN-044). */
  readonly overloadedDayCount: number;
  /** Horas planejadas em serviços terceirizados — fora do saldo, mas nunca escondidas. */
  readonly servicePlannedQuarterHours: number;
  readonly serviceCount: number;
}

export interface WeeklyBoard {
  readonly isoYear: number;
  readonly isoWeek: number;
  readonly startDate: string;
  readonly endDate: string;
  readonly status: WeeklyPlanStatus;
  readonly days: readonly BoardDay[];
  /** O catálogo que a obra alcança — a lista de onde a alocação escolhe (RN-230). */
  readonly services: readonly BoardService[];
  readonly rows: readonly BoardRow[];
  /** Só as atividades que aparecem no quadro — as que têm alguma alocação na semana. */
  readonly activities: readonly BoardActivity[];
  /**
   * As equipes da obra que recebem planejamento, e as que alguma alocação da semana marca
   * (Fase 30, D10) — por nome. Vazia na obra sem equipe.
   */
  readonly teams: readonly BoardTeam[];
  readonly summary: WeekSummary;
}

export type BoardSort = 'BALANCE' | 'NAME' | 'TEAM';

export interface BuildWeeklyBoardInput {
  readonly week: IsoWeek;
  readonly status: WeeklyPlanStatus;
  readonly resources: readonly BoardResource[];
  readonly activities: readonly BoardActivity[];
  /** O catálogo que a obra alcança, para a linha saber dizer o nome do serviço. */
  readonly services?: readonly BoardService[];
  readonly allocations: readonly WeeklyAllocation[];
  readonly capacity: ResourceCapacityIndex;
  /**
   * As equipes da obra (Fase 30, D10). Entram as ativas e as que alguma alocação viva da semana
   * marca; as outras ficam de fora. Ausente = obra sem equipe.
   */
  readonly teams?: readonly BoardTeam[];
  /** Padrão: mais ocioso primeiro — quem pode receber trabalho aparece no topo. */
  readonly sort?: BoardSort;
  /** Calendário da obra, só para marcar no cabeçalho os dias em que o canteiro abre. */
  readonly projectWorkday?: (date: BusinessDate) => boolean;
}

const WEEKDAY_LABEL = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'] as const;

export function buildWeeklyBoard(input: BuildWeeklyBoardInput): WeeklyBoard {
  const weekDays = input.week.days();
  const days: BoardDay[] = weekDays.map((day, index) => ({
    date: day.toString(),
    weekdayLabel: WEEKDAY_LABEL[index]!,
    shortLabel: `${String(day.day).padStart(2, '0')}/${String(day.month).padStart(2, '0')}`,
    isWorkday: input.projectWorkday?.(day) ?? index < 5,
  }));

  const live = input.allocations.filter(countsTowardCapacity);
  const calculator = new CapacityCalculator(input.capacity, spansFromAllocations(live));

  // Indexado uma vez por (recurso → frente + serviço → dia). Montar a grade custa um passo
  // por alocação, não uma varredura por célula. A chave do meio junta os dois porque é ela
  // que virou a linha: a mesma frente com dois serviços são duas linhas.
  const byResource = new Map<ExecutionResourceId, Map<string, Map<string, WeeklyAllocation>>>();
  for (const allocation of live) {
    let byLine = byResource.get(allocation.resourceId);
    if (byLine === undefined) {
      byLine = new Map();
      byResource.set(allocation.resourceId, byLine);
    }
    const lineKey = `${allocation.activityId}|${allocation.serviceId ?? ''}`;
    let byDate = byLine.get(lineKey);
    if (byDate === undefined) {
      byDate = new Map();
      byLine.set(lineKey, byDate);
    }
    byDate.set(allocation.date, allocation);
  }

  const activityById = new Map(input.activities.map((activity) => [activity.activityId, activity]));
  const serviceById = new Map(
    (input.services ?? []).map((service) => [service.serviceId, service]),
  );

  const rows: BoardRow[] = input.resources.map((resource) => {
    const byLine =
      byResource.get(resource.resourceId) ?? new Map<string, Map<string, WeeklyAllocation>>();

    const activityRows: BoardActivityRow[] = [...byLine.entries()]
      .flatMap(([lineKey, byDate]): BoardActivityRow[] => {
        const [rawActivityId = '', rawServiceId = ''] = lineKey.split('|');
        const activity = activityById.get(rawActivityId as ActivityId);
        /* c8 ignore next -- atividade órfã já é omitida na montagem dos rótulos */
        if (activity === undefined) return [];
        // Serviço apagado do catálogo depois de planejado deixa a linha sem nome, e não
        // sem linha: as horas aconteceram (RN-234).
        const service =
          rawServiceId === '' ? undefined : serviceById.get(rawServiceId as ServiceId);

        const cells: BoardCell[] = days.map((day) => {
          const allocation = byDate.get(day.date);
          return allocation === undefined ? { date: day.date } : { date: day.date, allocation };
        });

        return [
          {
            activity,
            ...(service === undefined ? {} : { service }),
            cells,
            plannedQuarterHours: [...byDate.values()].reduce(
              (total, item) => total + item.quarterHours,
              0,
            ),
          },
        ];
      })
      // Pela frente primeiro, e dentro dela pelo serviço: é o que mantém juntas as linhas
      // que o cabeçalho vai agrupar. A linha sem serviço fica no fim da frente dela, onde
      // se vê que falta alguma coisa.
      .sort(
        (a, b) =>
          compareNames(a.activity.label, b.activity.label) ||
          compareNames(a.service?.name ?? '￿', b.service?.name ?? '￿'),
      );

    const dayTotals: BoardDayTotal[] = weekDays.map((day) => {
      const date = day.toString();
      const planned = activityRows.reduce((total, row) => {
        const cell = row.cells.find((item) => item.date === date);
        return total + (cell?.allocation?.quarterHours ?? 0);
      }, 0);
      const capacity = input.capacity.on(resource.resourceId, day);

      return {
        date,
        plannedQuarterHours: planned,
        status: capacityStatusOf(capacity, planned),
        ...(capacity === undefined ? {} : { capacityQuarterHours: capacity }),
      };
    });

    return {
      resource,
      activities: activityRows,
      dayTotals,
      plannedQuarterHours: activityRows.reduce((total, row) => total + row.plannedQuarterHours, 0),
      balance: calculator.forWeek(resource.resourceId, input.week),
    };
  });

  const usedActivityIds = new Set(live.map((allocation) => allocation.activityId));
  const activities = input.activities
    .filter((activity) => usedActivityIds.has(activity.activityId))
    .sort((a, b) => compareNames(a.label, b.label));

  // A equipe desativada só aparece se a semana ainda tem alguém marcado por ela: é o nome da
  // linha que já existe, e não uma opção para planejar.
  const markedTeamIds = new Set(
    live.flatMap((allocation) =>
      allocation.plannedTeamId === undefined ? [] : [allocation.plannedTeamId as string],
    ),
  );
  const teams = (input.teams ?? [])
    .filter((team) => team.isActive || markedTeamIds.has(team.teamId))
    .sort((a, b) => compareNames(a.name, b.name));

  return {
    isoYear: input.week.isoYear,
    isoWeek: input.week.isoWeek,
    startDate: input.week.startDate().toString(),
    endDate: input.week.endDate().toString(),
    status: input.status,
    days,
    services: input.services ?? [],
    rows: sortRows(rows, input.sort ?? 'BALANCE'),
    activities,
    teams,
    summary: summarize(rows),
  };
}

/**
 * Ordenação do quadro.
 *
 * O padrão é por saldo, do mais ocioso ao mais carregado — a pergunta que o planejador
 * abre a tela para responder é "quem ainda pode pegar serviço?" (Q04). Recurso sem
 * limite declarado não tem saldo comparável e vai para o fim, ordenado por nome.
 */
export function sortRows(rows: readonly BoardRow[], sort: BoardSort): BoardRow[] {
  const copy = [...rows];
  switch (sort) {
    case 'NAME':
      return copy.sort((a, b) => compareNames(a.resource.name, b.resource.name));
    case 'TEAM':
      // Quem não tem equipe vai para o fim — "sem equipe" não é um nome que se ordena
      // junto com os outros, é a ausência de um.
      return copy.sort((a, b) => {
        const left = a.resource.teamName;
        const right = b.resource.teamName;
        if (left !== right) {
          if (left === undefined) return 1;
          if (right === undefined) return -1;
          const byTeam = compareNames(left, right);
          if (byTeam !== 0) return byTeam;
        }
        return compareNames(a.resource.name, b.resource.name);
      });
    case 'BALANCE':
      return copy.sort((a, b) => {
        const left = a.balance.balanceQuarterHours;
        const right = b.balance.balanceQuarterHours;
        if (left === undefined && right === undefined) {
          return compareNames(a.resource.name, b.resource.name);
        }
        if (left === undefined) return 1;
        if (right === undefined) return -1;
        return right - left || compareNames(a.resource.name, b.resource.name);
      });
  }
}

/**
 * Resumo da semana — **da mão de obra própria**.
 *
 * O serviço terceirizado fica de fora de capacidade, planejado e saldo, e aparece à
 * parte. A razão é que os dois números respondem perguntas diferentes: a jornada de um
 * funcionário é hora que a obra **tem** e precisa ocupar; a capacidade declarada de uma
 * empreiteira é o quanto ela se comprometeu a entregar, e somá-la ao saldo faria a obra
 * parecer ter 90h livres de gente que ela não contratou por hora. O que a empreiteira
 * tem alocado continua visível, num contador próprio — fora do saldo, nunca escondido.
 *
 * A capacidade só conta a de quem **pode receber trabalho**: o servente afastado e o
 * pedreiro desligado continuam no quadro, para que suas alocações antigas apareçam, mas
 * a jornada deles não é mão de obra disponível. Somá-la prometeria 44h que não existem —
 * o mesmo erro que a semana do feriado comete, só que com gente em vez de dia.
 *
 * As horas planejadas, essas, contam sempre: hora planejada para quem entrou de férias é
 * exatamente o problema que o planejador precisa enxergar.
 */
function summarize(rows: readonly BoardRow[]): WeekSummary {
  let available = 0;
  let planned = 0;
  let overloaded = 0;
  let atLimit = 0;
  let withBalance = 0;
  let allocated = 0;
  let overloadedDays = 0;
  let servicePlanned = 0;
  let serviceCount = 0;

  for (const row of rows) {
    if (row.resource.type === 'SERVICE') {
      servicePlanned += row.plannedQuarterHours;
      if (row.plannedQuarterHours > 0) serviceCount += 1;
      continue;
    }

    planned += row.plannedQuarterHours;
    if (row.plannedQuarterHours > 0) allocated += 1;
    if (row.balance.availableQuarterHours !== undefined && row.resource.isAllocatable) {
      available += row.balance.availableQuarterHours;
    }
    overloadedDays += row.dayTotals.filter((day) => day.status === 'EXCEEDED').length;

    switch (row.balance.status) {
      case 'EXCEEDED':
        overloaded += 1;
        break;
      case 'AT_LIMIT':
        atLimit += 1;
        break;
      case 'WITHIN':
        if ((row.balance.balanceQuarterHours ?? 0) > 0 && row.resource.isAllocatable) {
          withBalance += 1;
        }
        break;
      case 'UNLIMITED':
        break;
    }
  }

  const balance = available - planned;

  return {
    availableQuarterHours: available,
    plannedQuarterHours: planned,
    balanceQuarterHours: balance,
    // Ociosidade só existe onde há capacidade declarada; sem limite, dividir por zero
    // produziria `NaN` na tela em vez de "sem limite".
    idleRatio: available === 0 ? 0 : Math.max(0, balance) / available,
    overloadedCount: overloaded,
    atLimitCount: atLimit,
    withBalanceCount: withBalance,
    allocatedResourceCount: allocated,
    overloadedDayCount: overloadedDays,
    servicePlannedQuarterHours: servicePlanned,
    serviceCount,
  };
}

export interface AvailabilityLine {
  readonly resource: BoardResource;
  readonly balance: CapacityBalance;
}

export interface ResourceAvailability {
  /** Q04 — quem tem saldo, do mais ocioso ao menos. */
  readonly withBalance: readonly AvailabilityLine[];
  /** Q05 — quem está sobrecarregado, do maior excesso ao menor. */
  readonly overloaded: readonly AvailabilityLine[];
  /** Sem limite declarado: nem ocioso nem sobrecarregado, e a tela precisa dizer isso. */
  readonly unlimited: readonly AvailabilityLine[];
}

/**
 * Q04 e Q05 a partir do quadro já montado — sem recalcular capacidade.
 *
 * "Quem tem saldo" responde *a quem posso dar serviço*, então lista apenas quem está
 * apto a receber (RN-054, RN-086). "Quem está sobrecarregado" não filtra: o afastado com
 * horas planejadas é justamente o caso que precisa aparecer.
 */
export function resourceAvailability(rows: readonly BoardRow[]): ResourceAvailability {
  const lines = rows.map((row) => ({ resource: row.resource, balance: row.balance }));

  return {
    withBalance: lines
      .filter((line) => line.resource.isAllocatable && (line.balance.balanceQuarterHours ?? 0) > 0)
      .sort(
        (a, b) =>
          (b.balance.balanceQuarterHours ?? 0) - (a.balance.balanceQuarterHours ?? 0) ||
          compareNames(a.resource.name, b.resource.name),
      ),
    overloaded: lines
      .filter((line) => line.balance.status === 'EXCEEDED')
      .sort(
        (a, b) =>
          (a.balance.balanceQuarterHours ?? 0) - (b.balance.balanceQuarterHours ?? 0) ||
          compareNames(a.resource.name, b.resource.name),
      ),
    unlimited: lines
      .filter((line) => line.balance.status === 'UNLIMITED')
      .sort((a, b) => compareNames(a.resource.name, b.resource.name)),
  };
}

/**
 * Atividade encerrada não entra na lista de alocáveis (RN-084), nem a de disciplina
 * desativada (RN-230, estendida à disciplina).
 */
export function isAllocatableActivity(activity: BoardActivity): boolean {
  return !isClosed(activity.status) && activity.disciplineInactive !== true;
}
